/* =================================================
   c_lines-canvas — 流線アニメーション（c_kv 由来の共有実装）
   任意の <canvas> に対して同じ流線ビジュアルを描画する。
   使い方：
     <canvas data-c-lines-canvas ...></canvas> を置くと自動で初期化される。
   c_kv.liquid はセクション固有の描画オフセットを持つため独自スクリプトのまま。
   本ファイルは c_footer-visual など「装飾背景として流線を使う」用途で共有する。
   ================================================= */
(function () {
  function initLinesCanvas(canvas) {
    if (!canvas || canvas.dataset.cLinesReady === 'true') return;
    canvas.dataset.cLinesReady = 'true';

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const SPREAD_MIN = 1.0;
    const SPREAD_MAX = 2.2;
    const PULSE_SPEED = 0.005;

    let seed = 88;
    function seededRandom() {
      const x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    }

    let cssWidth, cssHeight, dpr;
    let lines = [];
    let numLines = 120;
    let frameIntervalMs = 0;
    let nextDrawTime = 0;

    /* ------------------------------
      描画負荷の調整値（テーマ設定「流線アニメ（負荷・静止フォールバック）」）
    ------------------------------*/

    /*  値は snippets/c_lines-perf-attrs.liquid が data 属性で渡す。
        属性が無い場合は既定値（本数 120・DPR 上限 2・FPS 自動・太さ 100%）で動く。 */
    function readNumberAttr(key, fallback) {
      const v = parseFloat(canvas.dataset[key]);
      return isNaN(v) ? fallback : v;
    }
    const baseNumLines = readNumberAttr('cLinesCount', 120);
    const maxDpr = readNumberAttr('cLinesDpr', 2);
    const fpsCap = readNumberAttr('cLinesFps', 0); /*  0 = 自動（描画解像度で判定）  */
    const lineWidthPx = 2.6 * (readNumberAttr('cLinesWidth', 100) / 100);
    const dprScale = canvas.dataset.cLinesDprScale || 'off'; /*  auto / mild / off  */

    /*  DPR に応じて線本数を抑える（高解像度ほど 1 本あたりの塗り面積が増えるため）。
        削減の強さはテーマ設定「高解像度時の本数の自動削減」で切り替える。 */
    function resolveNumLines(pixelRatio) {
      if (dprScale === 'off') return baseNumLines;
      const mild = dprScale === 'mild';
      if (pixelRatio >= 2) return Math.round(baseNumLines * (mild ? 0.7 : 0.5));
      if (pixelRatio >= 1.5) return Math.round(baseNumLines * (mild ? 0.86 : 0.72));
      return baseNumLines;
    }

    /*  描画解像度（css × dpr²）に応じてフレームレート上限を設ける。
        フルビューポート×Retina は数M〜になりやすいので、極端な高解像度だけ間引く。
        設定で上限を選んだ場合も自動の間引きは併用し、「厳しい方（＝間隔が長い方）」を採る。
        例：上限 60fps ＋ ドット数 1200 万超 → 24fps。上限だけ／自動だけの二者択一にしない。 */
    function resolveFrameIntervalMs(drawPixels) {
      const capInterval = fpsCap > 0 ? 1000 / fpsCap : 0;
      let autoInterval = 0;
      if (drawPixels > 12000000) autoInterval = 1000 / 24;
      else if (drawPixels > 8000000) autoInterval = 1000 / 30;
      return Math.max(capInterval, autoInterval);
    }

    // 帯の中心軸（左端 x=0 での軸位置）を canvas 高さに対する割合で指定できる。
    // data-c-lines-baseline（PC）／data-c-lines-baseline-sp（〜980px）。未指定は 0.5（中央）。
    // 横長 canvas ほどチルトで帯が上に寄るため、コンテナごとに下げ幅を調整する用途。
    let baseline = 0.5;
    function computeBaseline() {
      const pc = parseFloat(canvas.dataset.cLinesBaseline);
      const sp = parseFloat(canvas.dataset.cLinesBaselineSp);
      let b = isNaN(pc) ? 0.5 : pc;
      if (window.matchMedia('(max-width: 980px)').matches && !isNaN(sp)) b = sp;
      baseline = b;
    }

    const colorSettings = [
      { color: '#7BBCEF', ratios: [10, 8, 8] },
      { color: '#2BA3F3', ratios: [6, 4] },
      { color: '#ED5A83', ratios: [6, 6, 4, 4, 2] },
      { color: '#FFEB7A', ratios: [12, 10, 8, 6] },
    ];

    let colorChunks = [];

    function setupColorChunks() {
      colorChunks = [];
      colorSettings.forEach(function (setting) {
        setting.ratios.forEach(function (ratio) {
          colorChunks.push({ color: setting.color, ratio: ratio, baseY: 0 });
        });
      });

      const numChunks = colorChunks.length;
      const baseOffsets = [];
      let currentY = 0;

      for (let i = 0; i < numChunks; i++) {
        baseOffsets.push(currentY);
        const makeGap = seededRandom() < 0.35;
        const gapSize = makeGap ? (seededRandom() * 7 + 0.5) : 0;
        currentY += 1 + gapSize;
      }

      const totalHeight = currentY - 1;
      baseOffsets.forEach(function (_, i) {
        baseOffsets[i] -= totalHeight / 2;
      });

      baseOffsets.sort(function () { return seededRandom() - 0.64; });

      colorChunks.forEach(function (chunk, i) {
        chunk.baseY = baseOffsets[i];
      });
    }

    function getRandomChunk() {
      const totalRatio = colorChunks.reduce(function (sum, item) { return sum + item.ratio; }, 0);
      let randomVal = seededRandom() * totalRatio;
      for (let i = 0; i < colorChunks.length; i++) {
        if (randomVal < colorChunks[i].ratio) return colorChunks[i];
        randomVal -= colorChunks[i].ratio;
      }
      return colorChunks[0];
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
      cssWidth = canvas.offsetWidth;
      cssHeight = canvas.offsetHeight;
      canvas.width = cssWidth * dpr;
      canvas.height = cssHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      numLines = resolveNumLines(dpr);
      frameIntervalMs = resolveFrameIntervalMs(cssWidth * cssHeight * dpr * dpr);
      computeBaseline();
    }

    function resetAndInitLines() {
      seed = 42;
      setupColorChunks();
      lines = [];

      for (let i = 0; i < numLines; i++) {
        const chunk = getRandomChunk();
        const yOffset = chunk.baseY + (seededRandom() - 0.5) * 1.2;
        lines.push({
          amplitude: Math.pow(seededRandom(), 3) * 35 + 10,
          frequency: seededRandom() * 0.0074 + 0.001,
          speed: seededRandom() * 0.0010 + 0.006,
          phase: seededRandom() * Math.PI * 5,
          lineWidth: lineWidthPx,
          yOffset: yOffset,
          color: chunk.color
        });
      }
      lines.sort(function (a, b) { return a.yOffset - b.yOffset; });
    }

    let resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        resize();
        resetAndInitLines();
        /*  静止後はループが止まっているのでリサイズ時だけ 1 コマ描き直す  */
        if (perfFrozen) animate();
      }, 100);
    });

    resize();
    resetAndInitLines();

    let time = 0;
    const STEP = 12;

    function getPoint(line, x, startY, slope) {
      const sineWave = Math.sin(time * PULSE_SPEED);
      const progress = (sineWave + 1) / 2;
      const pulse = SPREAD_MIN + (SPREAD_MAX - SPREAD_MIN) * progress;
      const spreadMultiplier = Math.max(0.1, pulse);
      const axisY = startY + (line.yOffset * spreadMultiplier) + (x * slope);
      const angle1 = (x * line.frequency) + (time * line.speed) + line.phase;
      const angle2 = (x * line.frequency * 1.5) - (time * line.speed * 0.5);
      const waveY = Math.cos(angle1) * line.amplitude + Math.cos(angle2) * (line.amplitude * 0.3);
      const waveZ = -(Math.sin(angle1) * line.amplitude + Math.sin(angle2) * (line.amplitude * 0.3));
      return {
        x: x,
        y: axisY + (waveY * spreadMultiplier),
        z: waveZ * spreadMultiplier
      };
    }

    function animate() {
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      const angle = 12;
      const slope = -Math.tan(angle * (Math.PI / 180));
      const startY = cssHeight * baseline;

      const drawList = [];
      lines.forEach(function (line) {
        const points = [];
        let zSum = 0;
        for (let x = 0; x <= cssWidth + STEP; x += STEP) {
          const p = getPoint(line, x, startY, slope);
          points.push(p);
          zSum += p.z;
        }
        drawList.push({
          points: points,
          z: zSum / points.length,
          color: line.color,
          lineWidth: line.lineWidth
        });
      });

      drawList.sort(function (a, b) { return a.z - b.z; });

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 1.0;

      drawList.forEach(function (item) {
        const pts = item.points;
        ctx.strokeStyle = item.color;
        ctx.lineWidth = item.lineWidth;
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x, pts[i].y);
        }
        ctx.stroke();
      });

      time -= 1;
    }

    /* ------------------------------
      描画ループ（画面内のときだけ回す）と低FPS時の静止フォールバック
    ------------------------------*/

    /*  ※ prefers-reduced-motion でも糸Canvasは再生する（ブランド表現のため）
        ＋実測FPSが低い環境だけ静止画へフォールバック（スペック推測ではなく実測）
        - 各しきい値はテーマ設定から data 属性で渡す（既定：3秒待機・2秒計測・15fps・2回連続）
        - 起動直後はフォント読込・画像デコードで必ず重くなるため WARMUP_MS の間は計測しない
        - 一時的なスタッル（GC・画像デコード）での誤判定を避けるため、PROBE_STRIKES 窓連続で
          下回ったときだけ静止させる。1 度でも基準を満たしたら計測終了 */
    let rafId = null;
    let running = false;
    let perfFrozen = false;
    const FPS_FALLBACK_ON = canvas.dataset.cLinesFpsFallback !== 'off';
    const WARMUP_MS = readNumberAttr('cLinesFpsWarmup', 3) * 1000;
    const PROBE_MS = readNumberAttr('cLinesFpsProbe', 2) * 1000;
    const FPS_MIN = readNumberAttr('cLinesFpsMin', 15);
    const PROBE_STRIKES = readNumberAttr('cLinesFpsStrikes', 2);
    let probing = false;
    let probeStrikes = 0;
    let warmupStart = 0;
    let probeFrames = 0;
    let probeStart = 0;

    function resetProbeWindow() {
      warmupStart = 0;
      probeStart = 0;
      probeFrames = 0;
    }

    /*  戻り値 true = 静止判定（呼び出し側でループを止める）  */
    function measureFps(t) {
      /*  バックグラウンドタブ中は rAF が止まるため計測をやり直す（誤って低FPS判定しない）  */
      if (document.hidden) {
        resetProbeWindow();
        return false;
      }
      if (!warmupStart) warmupStart = t;
      if (t - warmupStart < WARMUP_MS) return false;
      if (!probeStart) {
        /*  計測窓の起点。このフレームは数えない（elapsed と frames をずらさない）  */
        probeStart = t;
        probeFrames = 0;
        return false;
      }
      probeFrames += 1;
      const elapsed = t - probeStart;
      if (elapsed < PROBE_MS) return false;

      const fps = probeFrames / (elapsed / 1000);
      probeStart = 0;
      probeFrames = 0;
      if (fps >= FPS_MIN) {
        probing = false;
        return false;
      }
      probeStrikes += 1;
      if (probeStrikes < PROBE_STRIKES) return false;
      probing = false;
      perfFrozen = true;
      return true;
    }

    function loop(now) {
      if (!running) return;
      const t = typeof now === 'number' ? now : performance.now();

      /*  フレームレート上限（0 のときは毎フレーム描画）  */
      if (frameIntervalMs <= 0) {
        animate();
      } else {
        if (!nextDrawTime) nextDrawTime = t;
        /*  許容誤差を持たせる：上限の間隔がディスプレイの更新間隔で割り切れないとき、
            厳密比較だと 1 コマ余分に飛んで設定値を 15〜20% 下回るため  */
        if (t >= nextDrawTime - frameIntervalMs * 0.1) {
          animate();
          /*  次回の基準は「実際に描いた時刻」ではなく前回の基準＋間隔にして位相を繰り越す
              （60Hz で 24fps のように割り切れない組み合わせでも平均を設定値に保つ）  */
          nextDrawTime += frameIntervalMs;
          /*  タブ復帰などで大きく遅れたら基準を引き直す（まとめ描きを防ぐ）  */
          if (nextDrawTime < t) nextDrawTime = t + frameIntervalMs;
        }
      }

      /*  計測は rAF の呼ばれた回数＝実際の表示フレームレートで行う（上限設定の影響を受けない）  */
      if (probing && measureFps(t)) {
        stop();
        return;
      }

      rafId = requestAnimationFrame(loop);
    }
    function start() {
      if (running || perfFrozen) return;
      running = true;
      nextDrawTime = 0;
      /*  中断（画面外・タブ非表示）を挟んだら計測窓はやり直す  */
      if (probing) resetProbeWindow();
      loop(performance.now());
    }
    function startWithProbe() {
      if (perfFrozen) return;
      /*  設定でオフのときは計測せず、そのまま再生し続ける  */
      if (FPS_FALLBACK_ON) {
        probing = true;
        probeStrikes = 0;
        resetProbeWindow();
      }
      start();
    }
    function stop() {
      running = false;
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      nextDrawTime = 0;
    }

    if ('IntersectionObserver' in window) {
      let hasProbed = false;
      const io = new IntersectionObserver(function (entries) {
        if (perfFrozen) return;
        if (entries[0].isIntersecting) {
          if (hasProbed) {
            start();
          } else {
            hasProbed = true;
            startWithProbe();
          }
        } else {
          stop();
        }
      }, { threshold: 0 });
      io.observe(canvas);
    } else {
      startWithProbe();
    }
  }

  function autoInit() {
    document.querySelectorAll('[data-c-lines-canvas]').forEach(initLinesCanvas);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

  window.CLinesCanvas = { init: initLinesCanvas };
})();
